import { Box, Center, HStack, Spinner, useToken } from '@chakra-ui/react'
import isEmpty from 'lodash/isEmpty'
import type { FC } from 'react'
import { useCallback, useEffect, useState, memo } from 'react'
import { Sparklines, SparklinesLine } from 'react-sparklines'

import { useColorModeValue } from '@app/components/ui/color-mode'
import useGetObject from '@app/hooks/useGetObject'
import useGetObjectProperty from '@app/hooks/useGetObjectProperty'
import useGetStrategyDateFilters from '@app/hooks/useGetStrategyDateFilters'
import { CardStat } from '@app/shared/cards/components'
import type { CardSize, DomainMetric, DomainNode } from '@app/types'
import { statLabel } from '@app/utils/metricHelpers'
import { loaderQuery } from '@graphql/client'
import { SampledMetricDataPoints } from '@graphql/documents/metric_data_point.graphql'
import { StatDisplayEnum } from '@graphql/types'

const MetricSparkline = ({ metric, strategyId, show = false }) => {
  const [sparklineLight, sparklineDark] = useToken('colors', ['blue.400', 'blue.500'])
  const sparklineColor = useColorModeValue(sparklineLight, sparklineDark)
  const [sampledMetricDataPoints, setSampledMetricDataPoints] = useState(null)
  const { id, updatedAt } = metric
  const strategy = useGetObject(strategyId, 'strategy')
  const { startDate, endDate, range } = strategy || {}

  const fetchMetricDataPoints = useCallback(async () => {
    await loaderQuery(SampledMetricDataPoints, { metricId: id, strategyId }).then((res) => {
      setSampledMetricDataPoints(res?.data?.sampledMetricDataPoints)
    })
  }, [id, strategyId])

  useEffect(() => {
    fetchMetricDataPoints().then(() => {})
    // Only update the sparkline when the metric updatedAt or strategy date filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt, startDate, endDate, range])

  if (!show) {
    return null
  }

  if (!sampledMetricDataPoints) {
    return (
      <Center display={show ? 'flex' : 'none'}>
        <Spinner size="xl" />
      </Center>
    )
  }

  const sparklineData = sampledMetricDataPoints?.map((datum) => datum.value) || []

  // Single data point sparklines are just a dot on the far left, so we don't want to render them or empty sets.
  if (isEmpty(sparklineData) || sparklineData.length < 2) {
    return null
  }

  return (
    <Box>
      <Sparklines height={50} data={sparklineData}>
        <SparklinesLine color={sparklineColor} />
      </Sparklines>
    </Box>
  )
}

interface Props {
  strategyId: string
  metric: Pick<
    DomainMetric,
    | 'id'
    | 'displayFormat'
    | 'rollUp'
    | 'periodToDate'
    | 'strategyStats'
    | 'positiveDirection'
    | 'compactDisplay'
    | 'maximumDisplayPrecision'
    | 'minimumDisplayPrecision'
    | 'redecimalPercentages'
  >
  node?: Pick<DomainNode, 'strategyStats'>
  size?: CardSize
}

const CardStats: FC<Props> = ({ strategyId, metric, node, size }) => {
  const { displayFormat } = metric
  const strategyStats = node?.strategyStats
  const statDisplay: StatDisplayEnum = useGetObjectProperty(strategyId, 'strategy', 'statDisplay')

  const strategyDateFilters = useGetStrategyDateFilters(strategyId)

  const includeAllStats = isEmpty(strategyDateFilters) || strategyDateFilters.range === 'trends'
  const stats = includeAllStats ? strategyStats : strategyStats?.slice(0, 1)

  const showStats = strategyStats && strategyStats.length > 0

  if (!showStats) {
    return null
  }

  const hideChange = stats.length < 3
  const showCardStats = statDisplay === StatDisplayEnum.Stats && stats.some((stat) => stat.total || stat.change)

  return (
    <>
      <MetricSparkline
        metric={metric}
        strategyId={strategyId}
        show={statDisplay === StatDisplayEnum.Charts && showStats}
      />
      <HStack justify="space-between">
        {showCardStats
          ? stats.map((stat) => (
              <CardStat
                key={`${metric?.id}-${stat.period}`}
                period={statLabel(stat, metric.rollUp, metric.periodToDate, strategyDateFilters)}
                total={stat.total}
                change={stat.change}
                displayFormat={displayFormat}
                metric={metric}
                size={size}
                hideChange={hideChange}
              />
            ))
          : null}
      </HStack>
    </>
  )
}

export default memo(CardStats)
